import * as React from "react";
import * as Pages from "@/components/Pages";
import csrftoken from "@/utils/csrf_token";

// The company FRONT PAGE: needle-moving outcomes in plain language. Agents post through the
// Highway (quota-gated by the feed_posts_per_day guardrail); the owner posts here, optionally
// tagged to a team -- the tagged team's agent is resolved automatically and woken with the post.
// A comment on an agent's post wakes that agent holding post + comment inline (zero lookup).

interface FeedComment {
  id: string;
  message: string;
  author: string | null;
  author_type: string | null;
  inserted_at: string;
}

interface FeedPost {
  id: string;
  kind: string; // "win" | "lesson" | "note"
  headline: string;
  body: string | null;
  metric: string | null;
  // Set when this post is about one email thread. Drives the "Open in Inbox" link.
  conversation_id: string | null;
  team: string | null;
  author: string | null;
  author_type: string | null;
  inserted_at: string;
  comments: FeedComment[];
}

interface Team {
  id: string;
  name: string;
}

const KIND_CHIP: Record<string, { label: string; className: string }> = {
  win: { label: "🏆 Win", className: "bg-callout-success-bg text-callout-success-content" },
  lesson: { label: "📚 Lesson", className: "bg-callout-warning-bg text-callout-warning-content" },
  note: { label: "📣 From the owner", className: "bg-callout-info-bg text-callout-info-content" },
};

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days}d ago`;
}

function Page() {
  // The page lives at /<companyId>/feed; its endpoints at /<companyId>/api/feed.
  const companyId = window.location.pathname.split("/")[1];

  const [posts, setPosts] = React.useState<FeedPost[]>([]);
  const [teams, setTeams] = React.useState<Team[]>([]);
  const [loaded, setLoaded] = React.useState(false);

  // Owner composer
  const [headline, setHeadline] = React.useState("");
  const [body, setBody] = React.useState("");
  const [teamId, setTeamId] = React.useState("");
  const [posting, setPosting] = React.useState(false);

  // Per-post comment drafts
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  const [sending, setSending] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      const res = await fetch(`/${companyId}/api/feed`, { headers: { Accept: "application/json" } });
      if (!res.ok) return;
      const data = await res.json();
      setPosts(data.posts || []);
      setTeams(data.teams || []);
      setLoaded(true);
    } catch {
      // keep whatever we have; the poll will retry
    }
  }, [companyId]);

  React.useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const submitPost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!headline.trim() || posting) return;
    setPosting(true);
    try {
      const res = await fetch(`/${companyId}/api/feed`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "x-csrf-token": csrftoken() || "" },
        body: JSON.stringify({ headline: headline.trim(), body: body.trim(), space_id: teamId }),
      });
      if (res.ok) {
        setHeadline("");
        setBody("");
        setTeamId("");
        await load();
      }
    } catch {
      // leave the composer filled so the human can retry
    }
    setPosting(false);
  };

  const submitComment = async (postId: string) => {
    const message = (drafts[postId] || "").trim();
    if (!message || sending) return;
    setSending(postId);
    try {
      const res = await fetch(`/${companyId}/api/feed/${postId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "x-csrf-token": csrftoken() || "" },
        body: JSON.stringify({ message }),
      });
      if (res.ok) {
        setDrafts((d) => ({ ...d, [postId]: "" }));
        await load();
      }
    } catch {
      // draft stays; the human can retry
    }
    setSending(null);
  };

  return (
    <Pages.Page title="Feed">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-content-accent mb-1">Feed</h1>
        <div className="text-sm text-content-dimmed mb-6">
          The front page: what actually moved the needle. Teams post wins and resolved lessons (quota-gated, plain
          language). Post something yourself and tag a team — the right agent is found and woken automatically; comment
          on a team's post and its agent wakes up holding your comment.
        </div>

        <form onSubmit={submitPost} className="border border-surface-outline rounded-2xl p-4 mb-8 bg-surface-base">
          <input
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder="Say something to the company or a team..."
            maxLength={200}
            data-test-id="feed-composer-headline"
            className="w-full text-base border border-surface-outline rounded-lg px-3 py-2 bg-surface-base focus:outline-none focus:ring-2 focus:ring-brand-1"
          />
          {headline.trim() && (
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="More detail (optional)..."
              rows={3}
              maxLength={2000}
              className="w-full text-sm border border-surface-outline rounded-lg px-3 py-2 mt-2 bg-surface-base focus:outline-none focus:ring-2 focus:ring-brand-1"
            />
          )}
          <div className="flex items-center gap-3 mt-3">
            <select
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              data-test-id="feed-composer-team"
              className="text-sm border border-surface-outline rounded-lg px-2 py-1.5 bg-surface-base"
            >
              <option value="">Whole company (no wake)</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  To: {t.name} (wakes their agent)
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={!headline.trim() || posting}
              data-test-id="feed-composer-post"
              className="ml-auto text-sm px-4 py-1.5 rounded-lg bg-brand-1 text-white-1 font-medium disabled:opacity-50"
            >
              {posting ? "Posting..." : "Post"}
            </button>
          </div>
        </form>

        {!loaded && <div className="text-sm text-content-dimmed">Loading the feed...</div>}
        {loaded && posts.length === 0 && (
          <div className="text-sm text-content-dimmed">
            Nothing on the front page yet. The first team win will show up here.
          </div>
        )}

        <div className="flex flex-col gap-4">
          {posts.map((p) => {
            const chip = KIND_CHIP[p.kind] || KIND_CHIP.note!;
            return (
              <div key={p.id} className="border border-surface-outline rounded-2xl p-4" data-test-id="feed-post">
                <div className="flex items-center gap-2 text-xs text-content-dimmed mb-2 flex-wrap">
                  <span className={"inline-flex items-center rounded-full px-2 py-0.5 font-medium " + chip.className}>
                    {chip.label}
                  </span>
                  <span className="font-medium text-content-base">{p.author || "Unknown"}</span>
                  {p.team && (
                    <span className="inline-flex items-center rounded-full border border-surface-outline px-2 py-0.5">
                      {p.team}
                    </span>
                  )}
                  <span>· {timeAgo(p.inserted_at)}</span>
                </div>

                <div className="font-semibold text-content-accent text-base">{p.headline}</div>
                {p.body && <div className="text-sm text-content-base mt-1 whitespace-pre-wrap">{p.body}</div>}
                {/*
                  A post about an email thread gets a direct route into it. New tab on purpose:
                  the Feed is a scanning surface and the owner is usually part-way down it, so
                  navigating away in place would cost the scroll position and the reading thread.
                */}
                {p.conversation_id && (
                  <div className="mt-3">
                    <a
                      href={`/${companyId}/admin/inbox?conversation=${encodeURIComponent(p.conversation_id)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-test-id="feed-open-in-inbox"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-1 border border-surface-outline rounded-lg px-2.5 py-1 hover:bg-surface-dimmed"
                    >
                      Open in Inbox
                      <span aria-hidden="true">&#8599;</span>
                    </a>
                  </div>
                )}
                {p.metric && (
                  <div className="inline-block text-sm font-medium text-brand-1 mt-2 border border-surface-outline rounded-lg px-2 py-0.5">
                    {p.metric}
                  </div>
                )}

                <div className="mt-3 pt-3 border-t border-surface-outline">
                    {p.comments.map((c) => (
                      <div key={c.id} className="mb-2">
                        <div className="text-xs text-content-dimmed">
                          <span className="font-medium text-content-base">{c.author || "Unknown"}</span> ·{" "}
                          {timeAgo(c.inserted_at)}
                        </div>
                        <div className="text-sm text-content-base whitespace-pre-wrap">{c.message}</div>
                      </div>
                    ))}

                    <div className="flex items-center gap-2 mt-2">
                      <input
                        value={drafts[p.id] || ""}
                        onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            submitComment(p.id);
                          }
                        }}
                        placeholder={
                          p.author_type === "ai" ? "Reply — wakes their agent with your comment..." : "Reply..."
                        }
                        maxLength={2000}
                        data-test-id="feed-comment-input"
                        className="flex-1 text-sm border border-surface-outline rounded-lg px-3 py-1.5 bg-surface-base focus:outline-none focus:ring-2 focus:ring-brand-1"
                      />
                      <button
                        onClick={() => submitComment(p.id)}
                        disabled={!(drafts[p.id] || "").trim() || sending === p.id}
                        className="text-sm px-3 py-1.5 rounded-lg border border-surface-outline font-medium disabled:opacity-50"
                      >
                        {sending === p.id ? "..." : "Reply"}
                      </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Pages.Page>
  );
}

export default { name: "FeedPage", loader: async () => ({}), Page };
